import { DataFarmType } from 'src/app/domainObjects/dataFarmType';
import { PreviewDataImportRequest } from 'src/app/messaging/previewImportDataRequest';
import { ConfigService } from 'src/app/services/config/config.service';
import { DataFarmViewModel } from 'src/app/viewModels/dataFarmViewModel';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { TransformerViewModel } from '../../../transformer-list/transformer-list.component';
import { AnonymizerRuleViewModel } from './anonymizerRuleViewModel';
import { TransformDetailViewModelBase } from '../../../dataTransformConfig/transformers/base/base-transformer-ui/transformDetailViewModelBase';
import { DataDictionaryService } from '../../../../services/dataDictionary/data-dictionary.service';
import { ValidationInfo, RequiredFieldValidation } from 'src/app/objectModel/validationInfo';
import { TransformerParametersViewModel } from '../transformer-parameters/transformerParametersViewModel';
import { AnonymizerTransform, AnonymizerTransformConfig } from 'src/app/domainObjects/transformers/anonymizerTransform';
import { AnonymizerTransformPreviewDataResponse } from 'src/app/messaging/anonymizerTransformPreviewDataResponse';
import { ColoredTextItem, NamedColorList } from 'src/app/domainObjects/coloredTextItem';
import { TransformType } from 'src/app/domainObjects/transformType';

export class AnonymizerViewModel extends TransformDetailViewModelBase<AnonymizerTransform> {
  farmType: DataFarmType;
  sampleStart : number;
  sampleEnd : number;
  runAll: boolean;
  // advanced settings
  threads: number;
  fixedTermsName:ColoredTextItem[]=[];
  fixedTermsCategory: ColoredTextItem[]=[];
  fixedTermsLocation: ColoredTextItem[]=[];
  fixedTermsDetails: ColoredTextItem[]=[];
  fixedTermsURL: ColoredTextItem[]=[];
  caseName: boolean=false ;
  caseCategory: boolean=false;
  caseLocation: boolean=false;
  caseDetails: boolean=false;
  caseURL: boolean=false;

  rules: Array<AnonymizerRuleViewModel> = [];

  parameters:TransformerParametersViewModel;
  dataFarm: DataFarmViewModel;
  // Preview options
  rows: number;
  // Status
  groupHitCount?: number;
  hitCount?: number;
  missCount?: number;
  hitRatio?: number;
  hitRatioDisplay?: string;

  request: PreviewDataImportRequest;
  response: AnonymizerTransformPreviewDataResponse;

    constructor(
      dataDictService: DataDictionaryService,
      private configService: ConfigService,
      configViewModel: DataTransformConfigViewModel,
      transformViewModel: TransformerViewModel) {
        super(dataDictService, configViewModel, transformViewModel);

        const transformConfig = this.transform.transformConfig;
        this.parameters=new TransformerParametersViewModel();
        if (transformConfig) {
          this.farmType = transformConfig.farmType ?? transformViewModel.transform.dataFarmType;
          this.sampleEnd = transformConfig.sampleEnd+1;
          this.sampleStart = transformConfig.sampleStart+1;
          this.runAll = transformConfig.runAll;
          this.sortInfo = transformConfig.sortInfo;
          //parameters
          this.parameters.sortInfo= transformConfig.sortInfo;
          this.parameters.sampleStart = transformConfig.sampleStart+ 1;
          this.parameters.sampleEnd = transformConfig.sampleEnd+ 1;
          this.parameters.runAll= transformConfig.runAll;
    
          // advanced settings
          this.threads = transformConfig.threads;
          let colorIndex = 0;
          this.fixedTermsName = transformConfig.fixedTermsName?.map(r => new ColoredTextItem(r, NamedColorList[colorIndex++])) ?? [];
          colorIndex = 0;
          this.fixedTermsCategory = transformConfig.fixedTermsCategory?.map(r => new ColoredTextItem(r, NamedColorList[colorIndex++])) ?? [];
          colorIndex = 0;
          this.fixedTermsLocation = transformConfig.fixedTermsLocation?.map(r => new ColoredTextItem(r, NamedColorList[colorIndex++])) ?? [];
          colorIndex = 0;
          this.fixedTermsDetails = transformConfig.fixedTermsDetails?.map(r => new ColoredTextItem(r, NamedColorList[colorIndex++])) ?? [];
          colorIndex = 0;
          this.fixedTermsURL = transformConfig.fixedTermsURL?.map(r => new ColoredTextItem(r, NamedColorList[colorIndex++])) ?? [];

          this.caseName = transformConfig.caseName;
          this.caseCategory = transformConfig.caseCategory;
          this.caseLocation = transformConfig.caseLocation;
          this.caseDetails = transformConfig.caseDetails;
          this.caseURL = transformConfig.caseURL;

          if (transformConfig.rules) {
            this.rules = transformConfig.rules.map(r => new AnonymizerRuleViewModel(r));
          }
        }
    }

    override configureValidationInfo(): ValidationInfo[] {
      const infos = [];
      infos.push( new RequiredFieldValidation('threads', 'Threads (0 > Auto)'));
      return infos;
    }

    reindexRules(): void {
      for (let i = 0; i < this.rules.length; i++) {
        this.rules[i].index = i + 1;
      }
      this.rules.sort((a, b) => a.index - b.index);
    }

    public override toTransformConfig(): AnonymizerTransformConfig {
      const config = new AnonymizerTransformConfig();
      config.farmType = this.farmType;
      config.transformType = TransformType.anonymizer;
      if(this.parameters.sortInfo){
        if(this.parameters.sortInfo.length==0){
          config.sortInfo =undefined;
        }else{
          config.sortInfo=this.parameters.sortInfo;
        }
      }
      config.sampleStart =this.parameters.sampleStart-1;
      config.sampleEnd =this.parameters.sampleEnd-1;
      config.runAll=this.parameters.runAll;

      config.threads = this.threads;
      config.fixedTermsName = this.fixedTermsName?.map(i => i.text);
      config.fixedTermsCategory = this.fixedTermsCategory?.map(i => i.text);
      config.fixedTermsLocation = this.fixedTermsLocation?.map(i => i.text);
      config.fixedTermsDetails = this.fixedTermsDetails?.map(i => i.text);
      config.fixedTermsURL = this.fixedTermsURL?.map(i => i.text);
      config.caseName = this.caseName;
      config.caseCategory = this.caseCategory;
      config.caseLocation = this.caseLocation;
      config.caseDetails = this.caseDetails;
      config.caseURL = this.caseURL;
      
      config.rules = this.rules.map(r => {
        return r.toRule();
      });

      return config;
    }

    public override onTransformRunCompleted(response: AnonymizerTransformPreviewDataResponse): void {
      // A cache-hit run carries no stats at all (they're deliberately stripped
      // before a CachedProcessingResult is persisted -- see
      // project_stats_vs_fullrun_separation memory). Nothing has changed since
      // whatever last populated the stats currently on screen, so they're still
      // accurate -- leave them exactly as they are instead of wiping them blank.
      if (
        !response?.previewStats ||
        !response?.totalStats ||
        !response.totalStats.ruleStats ||
        !response.previewStats.ruleStats
      ) {
        return;
      }

      // Clear all stats prior to rebinding new values
      this.rows = 0;
      this.rules.forEach( r => {
        r.rowHits = null;
        r.sampleRowHits = null;
      });
      
      this.rows = this.parameters.runAll ? response.currentData?.totalRows : response.activeData?.totalRows;
      // map the rule stats to the rule
      if (response.previewStats.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const ruleStat = response.previewStats.ruleStats.find(r => r.ruleIndex === i);
          //this.rules[i].sampleGroupHits = ruleStat?.groupHits;
          this.rules[i].sampleRowHits = ruleStat?.rowHits;
        }
      }
      // map the rule stats to the rule
      if (response.totalStats.ruleStats) {
        for (let i = 0; i < this.rules.length; i++) {
          const ruleStat = response.totalStats.ruleStats.find(r => r.ruleIndex === i);
          //this.rules[i].groupHits = ruleStat?.groupHits;
          this.rules[i].rowHits = ruleStat?.rowHits;
        }
      }
    }

}
